import { useEffect, useState } from 'react'
import arrowRight from '../../../../assets/icons/arrowRight.svg'
import { useDispatch, useSelector } from 'react-redux'
import { AppDispatch, RootState } from '../../../../redux'
import stepSlice from '../../../../redux/slice/steps.slice'
import { FamilyInfo, SessionsPerCampers } from '../../../../types/libertyLake'

const isWithinEarlyPayWindow = (date: Date): boolean => {
  const year = date.getFullYear()
  const start = new Date(year, 6, 15, 0, 0, 0, 0) // Jul 15
  const end = new Date(year, 8, 30, 23, 59, 59, 999) // Sep 30
  return date >= start && date <= end
}

const getNewFamilyDiscount = (sessionsPerCampers: SessionsPerCampers[]): number => {
  const totalWeeksAcrossFamily = sessionsPerCampers.reduce((sum, s) => sum + (s.weeks?.length || 0), 0)
  if (totalWeeksAcrossFamily < 3) return 0

  const campersWithAnyWeeks = sessionsPerCampers.filter(s => (s.weeks?.length || 0) > 0).length
  const eligibleCamperCount = Math.min(4, campersWithAnyWeeks)
  return Math.min(eligibleCamperCount * 250, 1000)
}

const Step3 = () => {
  const dispatch: AppDispatch = useDispatch()
  const { libertyLakeData, sessionsPerCampers, familyInfo, pricingMap } = useSelector((state: RootState) => state.steps)

  const isEarlyPayDiscountAvailable = isWithinEarlyPayWindow(new Date())

  const [amTransportationMeansList, setAmTransportationMeansList] = useState(['Central Express Bus (incl.)', 'Early Drop-off (incl)'])
  const [pmTransportationMeansList, setPmTransportationMeansList] = useState(['Central Express Bus (incl.)', 'Late Pick-up 4:15 PM - 6:00 PM (incl)'])

  const [occupationsList, setOccupationsList] = useState<string[]>([])
  const [occupationsDiscount, setOccupationsDiscount] = useState<any>(null)
  const [occupationDropdown, showOccupationDropdown] = useState(false)
  const [amDropdown, showAmDropdown] = useState(false)
  const [pmDropdown, showPmDropdown] = useState(false)

  useEffect(() => {
    if (libertyLakeData) {
      const OccupationDiscountData = libertyLakeData.Discount.Occupational
      setOccupationsDiscount(OccupationDiscountData)
      setOccupationsList(Object.keys(OccupationDiscountData))
      const transportationData = Object.keys(libertyLakeData.Discount.Transportation)
      transportationData.forEach(td => {
        if (td.includes('Drop-off') && amTransportationMeansList.length === 2) {
          setAmTransportationMeansList(prevArray => [...prevArray, td])
        } else if (td.includes('Pick') && pmTransportationMeansList.length === 2) {
          setPmTransportationMeansList(prevArray => [...prevArray, td])
        }
      })
    }
  }, [libertyLakeData])

  useEffect(() => {
    if (!isEarlyPayDiscountAvailable && familyInfo.isEarlyPaying) {
      dispatch(
        stepSlice.actions.setFamilyInfo({
          ...familyInfo,
          isEarlyPaying: false,
          earlyPayDiscount: 0
        })
      )
    }
  }, [dispatch, familyInfo, isEarlyPayDiscountAvailable])

  useEffect(() => {
    const familySessionsCost = getAllCampersSessionPrice(sessionsPerCampers)

    // transportation discount
    const flatTransportationDiscount =
      familyInfo.uniqueSessions.length * familyInfo.amTransportationDiscount + familyInfo.uniqueSessions.length * familyInfo.pmTransportationDiscount
    const amountAfterTransportationDiscount = familyInfo.familyPrice - flatTransportationDiscount

    // New Family discount (flat, before % discounts)
    const newFamilyDiscount = familyInfo.isNewCampFamily ? getNewFamilyDiscount(sessionsPerCampers) : 0
    const amountAfterNewFamilyDiscount = Math.max(0, amountAfterTransportationDiscount - newFamilyDiscount)

    // 4. Discount Hierarchy - occupational discount
    const amountAfterOccupationalDiscount = amountAfterNewFamilyDiscount * (1 - familyInfo.occupationalDiscountPercentage)

    // 5. Discount Hierarchy - early bird discount
    let earlyPayDiscount = 0
    sessionsPerCampers.forEach(sessionsPerCamper => {
      earlyPayDiscount += familyInfo.isEarlyPaying ? sessionsPerCamper.weeks.length * 25 : 0
    })

    const grandTotalPrice = amountAfterOccupationalDiscount - earlyPayDiscount

    const updatedFamilyDetails: FamilyInfo = {
      ...familyInfo,
      newFamilyDiscount,
      earlyPayDiscount,
      familySessionsCost,
      totalFamilyDiscount: familySessionsCost - grandTotalPrice,
      grandTotalFamilyCost: grandTotalPrice
    }

    dispatch(stepSlice.actions.setFamilyInfo(updatedFamilyDetails))
  }, [
    familyInfo.occupation,
    familyInfo.isEarlyPaying,
    familyInfo.isNewCampFamily,
    familyInfo.occupationalDiscountPercentage,
    familyInfo.amTransportationDiscount,
    familyInfo.pmTransportationDiscount,
    familyInfo.familyPrice,
    familyInfo.uniqueSessions,
    sessionsPerCampers
  ])

  const getAllCampersSessionPrice = (sessionsPerCampers: SessionsPerCampers[]): number => {
    let sum = 0
    sessionsPerCampers.forEach(sessionForCamper => {
      sessionForCamper.weeks.forEach(week => {
        sum += (pricingMap && pricingMap[week].price) || 0
      })
    })
    return sum
  }

  const getOccupationalDiscountPercentage = (occupation: string) => {
    return occupationsDiscount[occupation].Percentage
  }

  // const getTransportationDiscountAmount = (transportationMean: string) => {
  //   if (transportationDiscountMap && transportationDiscountMap[transportationMean]) {
  //     return transportationDiscountMap[transportationMean].Amount
  //   }
  //   return 0
  // }

  const toggleNewFamily = () => {
    const updatedInfo: FamilyInfo = {
      ...familyInfo,
      isNewCampFamily: !familyInfo.isNewCampFamily, // Toggle the value
      newFamilyDiscount: !familyInfo.isNewCampFamily ? getNewFamilyDiscount(sessionsPerCampers) : 0
    }
    dispatch(stepSlice.actions.setFamilyInfo(updatedInfo))
  }

  return <>
    <div className='step3'>
      <div className="formArea">
        <label className='formTitle'>Calculate Discount</label>
        {/* Intentionally Commented Transportation Section since we dont have Transportation Discounts */}
        {/* <h4 className='transportationMeans'>What are your transportation preferences?</h4>
        <div className='amPmRow'>
          <div className="inputFieldArea">
            <label className='fieldLabel'>AM Choice</label>
            <div
              className="selectInputFieldArea"
              onClick={() => {
                showAmDropdown(true)
              }}
            >
              {familyInfo.amTransportationChoice.length ? familyInfo.amTransportationChoice : 'Select...'}
              <img className='arrowIcon' src={arrowRight} />
            </div>
            <label className='helpText'>* Get $25/week discount on dropping off your kids to Camp</label>
            {amDropdown
              ? (
                <div className="dropdownContainer">
                  {amTransportationMeansList.map((transportationMean: string, index: number) => (
                    <div
                      className='dropdownOption'
                      key={`${transportationMean}:${index}`}
                      onClick={() => {
                        const updatedInfo: FamilyInfo = {
                          ...familyInfo,
                          amTransportationChoice: transportationMean,
                          amTransportationDiscount: getTransportationDiscountAmount(transportationMean)
                        }
                        dispatch(stepSlice.actions.setFamilyInfo(updatedInfo))
                        showAmDropdown(false)
                      }}
                    >
                      {transportationMean}
                    </div>
                  ))}
                </div>
              )
              : ''}
          </div>
          <div className="inputFieldArea">
            <label className='fieldLabel'>PM Choice</label>
            <div
              className="selectInputFieldArea"
              onClick={() => {
                showPmDropdown(true)
              }}
            >
              {familyInfo.pmTransportationChoice.length ? familyInfo.pmTransportationChoice : 'Select...'}
              <img className='arrowIcon' src={arrowRight} />
            </div>
            <label className='helpText'>* Get $25/week discount on picking up your kids from Camp</label>
            {pmDropdown
              ? (
                <div className="dropdownContainer">
                  {pmTransportationMeansList.map((transportationMean: string, index: number) => (
                    <div
                      className='dropdownOption'
                      key={`${transportationMean}:${index}`}
                      onClick={() => {
                        const updatedInfo: FamilyInfo = {
                          ...familyInfo,
                          pmTransportationChoice: transportationMean,
                          pmTransportationDiscount: getTransportationDiscountAmount(transportationMean)
                        }
                        dispatch(stepSlice.actions.setFamilyInfo(updatedInfo))
                        showPmDropdown(false)
                      }}
                    >
                      {transportationMean}
                    </div>
                  ))}
                </div>
              )
              : ''}
          </div>
        </div> */}

        <div className="inputFieldArea">
          <label className='fieldLabel'>Are you active military or one of the following occupation?</label>
          <div
            className="selectInputFieldArea"
            onClick={() => showOccupationDropdown(true)}
          >
            {familyInfo.occupation.length > 0 ? familyInfo.occupation : 'Select...'}
            <img className='arrowIcon' src={arrowRight} />
          </div>
          <label className='helpText'>
            * {(familyInfo.occupationalDiscountPercentage * 100)}% discount will be applied for your occupation: {familyInfo.occupation}
          </label>
          {occupationDropdown
            ? (
              <div className="dropdownContainer">
                {occupationsList.map((occupation: string, index: number) => (
                  <div
                    className='dropdownOption'
                    key={`${occupation}:${index}`}
                    onClick={() => {
                      const updatedInfo: FamilyInfo = {
                        ...familyInfo,
                        occupation,
                        occupationalDiscountPercentage: getOccupationalDiscountPercentage(occupation)
                      }
                      dispatch(stepSlice.actions.setFamilyInfo(updatedInfo))
                      showOccupationDropdown(false)
                    }}
                  >
                    {occupation}
                  </div>
                ))}
              </div>
            )
            : ''}
        </div>
        {/* If Yes (checked) and your family registers for 3+ total weeks: $250 off per camper (up to 4 campers; $1000 max per family) */}
        <div className='earlyPayDiscountArea' style={{ marginBottom: '20px' }}>
          <label className='fieldLabel' style={{ marginRight: '10px' }}>Check the box if you are a New Camp Family to Liberty Lake</label>
          <div className='checkboxArea'>
            <input
              type='checkbox'
              checked={familyInfo.isNewCampFamily}
              className='checkbox'
              onChange={toggleNewFamily}
            />
            <label
              className='checkmark'
              onClick={toggleNewFamily}
            />
          </div>
        </div>
        {/* Pay-in-Full option visibility: show from July 15 through Sept 30 (inclusive); hide from Oct 1 through July 14 (inclusive). */}
        {isEarlyPayDiscountAvailable && (
          <>
            <div className='earlyPayDiscountArea'>
              <label className='fieldLabel' style={{ marginRight: '10px' }}>Will you pay in full by Sept 30 for an additional discount?</label>
              <div className='checkboxArea'>
                <input type='checkbox' checked={familyInfo.isEarlyPaying} className='checkbox' onChange={() => { }} />
                <label
                  className='checkmark'
                  onClick={() => {
                    const isEarlyPaying = !familyInfo.isEarlyPaying
                    let earlyPayDiscount = 0
                    sessionsPerCampers.forEach(sessionsPerCamper => {
                      earlyPayDiscount += isEarlyPaying ? sessionsPerCamper.weeks.length * 25 : 0
                    })
                    const updatedInfo: FamilyInfo = {
                      ...familyInfo,
                      isEarlyPaying,
                      earlyPayDiscount
                    }
                    dispatch(stepSlice.actions.setFamilyInfo(updatedInfo))
                  }}
                />
              </div>
            </div>
            <label className='helpText'>* $25 off per week per camper</label>
          </>
        )}
      </div>
    </div>
    {(occupationDropdown || amDropdown || pmDropdown) && <div className='backDrop' onClick={() => {
      showOccupationDropdown(false)
      showAmDropdown(false)
      showPmDropdown(false)
    }} />}
  </>
}

export default Step3
